import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Tag, ArrowRight, Clock, ShieldCheck, MapPin, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { formatPrice } from "@/components/PropertyCard";

function DealCard({ p }) {
  const discounted = p.discount_pct ? Math.round(p.price_min * (1 - p.discount_pct / 100)) : null;
  return (
    <Link
      to={`/projects/${p.id}`}
      data-testid={`hot-deal-card-${p.id}`}
      className="group bg-white border border-amber-500/20 hover:border-[#D4A437] transition-colors overflow-hidden flex flex-col"
    >
      <div className="relative aspect-[16/10] bg-slate-50 overflow-hidden">
        <img
          src={p.images?.[0] || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200"}
          alt={p.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
        {p.discount_pct && (
          <div className="absolute top-4 left-4 bg-[#D4A437] text-white px-3 py-1.5 flex items-center gap-1.5 font-medium text-xs tracking-wider">
            <Flame className="w-3.5 h-3.5" strokeWidth={2.5} fill="currentColor" />
            {p.discount_pct}% OFF
          </div>
        )}
        {p.deal_label && (
          <div className="absolute top-4 right-4 bg-white/80 backdrop-blur border border-slate-200 text-[#B88C2E] text-[10px] tracking-[0.2em] uppercase px-3 py-1.5">
            {p.deal_label}
          </div>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="overline mb-2 flex items-center gap-1.5">
          <MapPin className="w-3 h-3" /> {p.locality}, {p.city}
        </div>
        <h3 className="font-serif text-xl text-slate-900 group-hover:text-[#B88C2E] transition-colors">{p.name}</h3>
        <div className="text-xs text-slate-500 mt-1">By {p.developer}</div>

        <div className="mt-5 flex items-end gap-3">
          {discounted ? (
            <>
              <div className="font-serif text-2xl text-[#D4A437]">{formatPrice(discounted, p.currency)}</div>
              <div className="text-sm text-slate-500 line-through pb-1">{formatPrice(p.price_min, p.currency)}</div>
            </>
          ) : (
            <div className="font-serif text-2xl text-[#D4A437]">{formatPrice(p.price_min, p.currency)}</div>
          )}
        </div>
        <div className="text-[10px] text-slate-500 mt-1">starting · indicative</div>

        <div className="mt-auto pt-6 flex items-center justify-between">
          <div className="text-xs text-slate-600">{p.bhk?.length ? p.bhk.join(", ") + " BHK" : "—"} · {p.area_min}-{p.area_max} sqft</div>
          <span className="text-xs text-[#D4A437] inline-flex items-center gap-1 tracking-[0.15em] uppercase">
            View <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function HotDeals() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects?hot_deal=true").then((r) => setDeals(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="hot-deals-page" className="pb-32">
      {/* Hero */}
      <section className="relative pt-24 pb-16 border-b border-amber-500/20 bg-gradient-to-b from-amber-500/5 to-transparent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline mb-5 flex items-center gap-2 text-[#D4A437]">
            <Flame className="w-3.5 h-3.5" fill="currentColor" /> Hot Deals · Limited Inventory
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-900 font-light leading-[1.05] max-w-4xl">
            Discounted prices.<br />
            <em className="text-[#D4A437] not-italic">Real, ratified, RERA-compliant.</em>
          </h1>
          <p className="text-slate-700 text-lg mt-8 max-w-2xl font-light leading-relaxed">
            Hand-picked allocations from developers, festive launch offers and resale opportunities — vetted by our
            in-house desk so you know the discount is real, the title clean and the timeline genuine.
          </p>
          <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-600">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4A437]" /> RERA-verified discounts</span>
            <span className="inline-flex items-center gap-2"><Tag className="w-4 h-4 text-[#D4A437]" /> Up to 15% off list price</span>
            <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-[#D4A437]" /> Refreshed weekly</span>
          </div>
        </div>
      </section>

      {/* Deals grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16">
        {loading ? (
          <div className="py-32 text-center text-slate-600 inline-flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading current deals...
          </div>
        ) : deals.length === 0 ? (
          <div className="py-32 text-center">
            <p className="text-slate-600 font-light">No active deals right now. Check back soon.</p>
            <Link to="/projects" className="inline-block mt-6 text-[#D4A437] hover:text-[#B88C2E] text-sm tracking-[0.15em] uppercase">
              Browse all projects →
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="overline mb-2">Active right now</div>
                <h2 className="font-serif text-2xl text-slate-900">{deals.length} deal{deals.length !== 1 ? "s" : ""} live</h2>
              </div>
              <p className="text-xs text-slate-500 max-w-xs">
                Indicative pricing. Final discount confirmed by developer at booking. Some offers are subject to inventory.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hot-deals-grid">
              {deals.map((p) => (<DealCard key={p.id} p={p} />))}
            </div>
          </>
        )}
      </section>

      {/* CTA strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32 bg-[#D4A437]/10 border border-amber-200 p-10 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <div className="overline mb-3 text-[#D4A437]">Off-market &amp; private deals</div>
          <h2 className="font-serif text-2xl md:text-3xl text-slate-900 font-light">Looking for an unlisted opportunity?</h2>
          <p className="text-slate-600 mt-3 max-w-2xl font-light">
            Some of our best deals never appear on this page. If you have a specific city, budget or developer in mind,
            tell our desk — we'll match you with off-market and pre-launch inventory.
          </p>
        </div>
        <Link
          to="/nri-services#nri-contact"
          data-testid="hot-deals-cta-private"
          className="inline-flex items-center gap-2 bg-[#D4A437] hover:bg-[#D4A437] text-white font-medium tracking-wide px-7 py-3.5 transition-colors shrink-0"
        >
          Request Private Deal <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
}
